// =============================================================================
// Calendar grid helpers — pure functions shared by the month grid, the day
// panel and the event handlers. They take the event list explicitly (the
// component used to close over it) so they can be unit-tested on their own.
// =============================================================================

import { format, startOfMonth, startOfWeek, addDays, parseISO } from 'date-fns';
import { expandEvents } from '../../utils/recurrence';

// Build the calendar grid (6 weeks starting Monday). Parameterized by
// `baseDate` so we can also render the previous/next month grids during
// a swipe transition.
export const getWeeksForMonth = (baseDate) => {
  const monthStart = startOfMonth(baseDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  // Always render exactly 6 weeks so adjacent months in the carousel
  // (which uses flex with align-items: stretch) have matching heights
  // and the current month doesn't get an empty row appended when a
  // neighbour is taller.
  const days = [];
  for (let i = 0; i < 42; i++) days.push(addDays(gridStart, i));
  const out = [];
  for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
  return out;
};

// Every entry that touches `date` (multi-day ones included), earliest first.
export const eventsForDay = (events, date) => {
  const dateStr = format(date, 'yyyy-MM-dd');
  return events
    .filter((ev) => {
      if (ev.endDate && ev.endDate !== ev.date) {
        return dateStr >= ev.date && dateStr <= ev.endDate;
      }
      return ev.date === dateStr;
    })
    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
};

// Multi-day all-day events spanning across a week row, with stacking
export const multiDayBarsForWeek = (events, week) => {
  const weekStartStr = format(week[0], 'yyyy-MM-dd');
  const weekEndStr = format(week[6], 'yyyy-MM-dd');

  const candidates = events
    .filter(
      (ev) =>
        ev.allDay !== false &&
        ev.endDate &&
        ev.endDate !== ev.date &&
        ev.date <= weekEndStr &&
        ev.endDate >= weekStartStr
    )
    .map((ev) => {
      const startIdx = week.findIndex((d) => format(d, 'yyyy-MM-dd') >= ev.date);
      const endIdxRev = [...week].reverse().findIndex((d) => format(d, 'yyyy-MM-dd') <= ev.endDate);
      const endIdx = 6 - endIdxRev;
      return {
        event: ev,
        startCol: Math.max(0, startIdx),
        endCol: Math.min(6, endIdx),
        continuesLeft: ev.date < weekStartStr,
        continuesRight: ev.endDate > weekEndStr
      };
    })
    .sort((a, b) => {
      // Longer events first, then by start date
      const lenA = a.endCol - a.startCol;
      const lenB = b.endCol - b.startCol;
      if (lenB !== lenA) return lenB - lenA;
      return a.startCol - b.startCol;
    });

  // Assign stacking row
  const rowEnds = []; // rowEnds[row] = last occupied col
  return candidates.map((bar) => {
    let row = 0;
    while (rowEnds[row] !== undefined && rowEnds[row] >= bar.startCol) row++;
    rowEnds[row] = bar.endCol;
    return { ...bar, row };
  });
};

// Compare old vs new event payloads. Returns the list of changed
// logical fields (used for the audit log summary), or [] if nothing
// meaningful changed.
export const diffEventFields = (oldEv, newEv) => {
  const changed = [];
  if ((oldEv.title || '') !== (newEv.title || '')) changed.push('title');
  const dateChanged =
    oldEv.date !== newEv.date ||
    (oldEv.endDate || oldEv.date) !== (newEv.endDate || newEv.date) ||
    (oldEv.startTime || '') !== (newEv.startTime || '') ||
    (oldEv.endTime || '') !== (newEv.endTime || '') ||
    !!oldEv.allDay !== !!newEv.allDay;
  if (dateChanged) changed.push('date');
  if ((oldEv.categoryId || '') !== (newEv.categoryId || '')) changed.push('label');
  if ((oldEv.notes || '') !== (newEv.notes || '')) changed.push('notes');
  if (
    JSON.stringify(oldEv.participantIds || []) !==
    JSON.stringify(newEv.participantIds || [])
  )
    changed.push('participants');
  if (
    JSON.stringify(oldEv.recurrence || null) !==
    JSON.stringify(newEv.recurrence || null)
  )
    changed.push('recurrence');
  return changed;
};

// --- Editing one occurrence, or from one occurrence on ------------------
// The form shows the series' *start* date. If the user changed that date,
// the occurrence they clicked moves by the same number of days; a multi-day
// event keeps its length.
export const dayShift = (from, to) => Math.round((Date.parse(to) - Date.parse(from)) / 86400000);
export const shiftDate = (iso, days) => format(addDays(parseISO(iso), days), 'yyyy-MM-dd');

// The series minus one occurrence: an exception, or a later start when it
// is the first one. Null when nothing would be left.
export const seriesWithoutOccurrence = (target, occurrenceDate) => {
  if (target.date !== occurrenceDate) {
    const exceptions = [...(target.recurrence.exceptions || []), occurrenceDate];
    return { ...target, recurrence: { ...target.recurrence, exceptions } };
  }
  const from = addDays(parseISO(occurrenceDate), 1);
  const next = expandEvents([target], from, addDays(from, 365 * 5)).find((o) => o.date > occurrenceDate);
  if (!next) return null;
  const span = dayShift(target.date, target.endDate || target.date);
  return { ...target, date: next.date, endDate: shiftDate(next.date, span) };
};
